import { getLocale } from "./locale.js";
import { formatMoney } from "./money.js";
import { digitsOf, firstName } from "./names.js";
import { dayOf, formatFullDay, formatTimeOfDay, obligationPeriodLabel, timeOf } from "./period.js";
/**
 * The reminder an owner sends, as text.
 *
 * THE SENDING IS NOT HERE, and that separation is the product position as much
 * as a technical one. There is no WhatsApp Business API, no scheduling and no
 * automation: something composes a message, hands it to WhatsApp, and a human
 * decides whether to press send. An app that could message a customer's parents
 * by itself is a very different conversation to have with a prospect — and a
 * different privacy policy.
 *
 * What each client does with the result differs, which is why only this half is
 * shared: the phone opens a `whatsapp://` URL and falls back to the clipboard,
 * a browser opens `api.whatsapp.com` in a tab. The words are the same either
 * way, and so are the addresses: `whatsappLinks` below writes every one.
 */
/**
 * The reminder the owner sends. Friendly on purpose: the person receiving it is
 * a parent who probably just forgot, and the business will see them at practice
 * on Saturday. Nothing in here says "moroso".
 */
export const DEFAULT_REMINDER_TEMPLATE = "Hola {cliente} 👋\n\n" +
    "Te recordamos que está pendiente el cobro de {miembro} correspondiente a {mes} por {monto}.\n\n" +
    "Muchas gracias.\n{negocio}";
/**
 * `"6000-0000"` → `"50760000000"`.
 *
 * WhatsApp wants digits with a country code and nothing else. A local
 * number gets the business's own country code in front; a number that
 * already carries it is left alone. Anything else returns null, and the caller says
 * so rather than opening WhatsApp on a chat with nobody.
 */
export function normalizePhoneForWhatsApp(raw, locale = getLocale()) {
    const digits = digitsOf(raw);
    const prefix = locale.phoneCountryCode;
    if (digits.length === locale.phoneDigits)
        return `${prefix}${digits}`;
    if (digits.startsWith(prefix) && digits.length === locale.phoneDigits + prefix.length) {
        return digits;
    }
    // Some other country's number, typed in full. Not our business to reformat.
    if (digits.length >= 10 && digits.length <= 15)
        return digits;
    return null;
}
export function buildReminderMessage(input) {
    const template = DEFAULT_REMINDER_TEMPLATE;
    return template
        .replaceAll("{cliente}", firstName(input.payerName))
        .replaceAll("{miembro}", input.memberName)
        // STILL CALLED {mes} although it renders a week too: the template is fixed
        // (anything more is anticipated complexity), and renaming a
        // placeholder nobody else writes buys nothing.
        .replaceAll("{mes}", obligationPeriodLabel(input.periodStart, input.frequency).toLowerCase())
        .replaceAll("{monto}", formatMoney(input.outstandingCents))
        .replaceAll("{negocio}", input.businessName);
}
/**
 * THE ONLY PLACE A WHATSAPP ADDRESS IS WRITTEN. `to` is digits with the
 * country code, already normalised, or null to let the sender pick the chat;
 * an empty text opens the chat with nothing written.
 *
 * Two URLs, because they fail in different places: the `whatsapp://` scheme
 * opens the app directly but is not registered when WhatsApp is not installed,
 * and the web address works through a browser. The web client opens `web`; the
 * phone hands both to `openOrCopy`.
 *
 * The web address is `api.whatsapp.com/send` and NEVER `wa.me`: `wa.me`
 * answers with a redirect that replaces every emoji (any 4-byte UTF-8
 * character) with «�», so the customer received «Hola Rolando �». The page
 * at `api.whatsapp.com` still draws its own preview bubble with «�», but what
 * it hands to the app and to WhatsApp Web keeps the emoji.
 */
export function whatsappLinks(to, text = "") {
    const params = [
        ...(to === null ? [] : [`phone=${to}`]),
        ...(text === "" ? [] : [`text=${encodeURIComponent(text)}`]),
    ];
    const query = params.length === 0 ? "" : `?${params.join("&")}`;
    return { app: `whatsapp://send${query}`, web: `https://api.whatsapp.com/send${query}` };
}
/**
 * WhatsApp, opened on somebody's chat with NOTHING WRITTEN IN IT.
 *
 * A cuota's reminder carries a message because the app knows what it is
 * chasing: a month, a member, a balance. A pedido's does not, and must not
 * pretend to -- the module has no idea what is being sold (spec §2.1), so any
 * sentence it composed would either name a trade or say nothing. The owner
 * opens the chat and writes what they were going to write anyway.
 *
 * `phone` is the number as stored. `null` when there is no usable number, so
 * a caller draws nothing rather than a button that opens a chat with nobody.
 */
export function whatsappChatLinks(phone, locale = getLocale()) {
    return whatsappSendLinks(phone, "", locale);
}
/**
 * `whatsappChatLinks` with a message already written in the chat's box: the
 * same number rule, and null without a usable number.
 */
/**
 * What «Copiar» says for a message about to be pasted into a chat, on both
 * clients: one text, since both say the same (`copyText` / `useCopyText`).
 */
export const MESSAGE_COPIED = { done: "Mensaje copiado", failed: "No se pudo copiar el mensaje. Inténtalo de nuevo." };
export function whatsappSendLinks(phone, text, locale = getLocale()) {
    if (phone === null)
        return null;
    const normalized = normalizePhoneForWhatsApp(phone, locale);
    if (normalized === null)
        return null;
    return whatsappLinks(normalized, text);
}
/** The appointment reminder a PERSON sends (citas spec §6.3). No money in it: it is a reminder of a time. */
export function appointmentReminder({ name, businessName, dueAt, tz }) {
    return `Hola ${name} 👋 Te recordamos tu cita el ${formatFullDay(dayOf(dueAt, tz))} a las ${formatTimeOfDay(timeOf(dueAt, tz))} en ${businessName}. Si no puedes venir, avísanos. ¡Gracias!`;
}
